import { cn } from "./classNames-BzrkDnOD.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { Button } from "./Button-uWJf4kxd.js";
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
import TextareaAutosize from "react-textarea-autosize";
//#region src/components/InputGroup/InputGroupAddon.tsx
const addonVariants = cva([
	"tw:flex",
	"tw:items-center",
	"tw:gap-1",
	"tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4",
	"tw:[&_svg]:shrink-0"
], {
	defaultVariants: { align: "inline-start" },
	variants: { align: {
		"inline-start": [
			"tw:order-first",
			"tw:shrink-0",
			"tw:[&:not(:empty)]:pl-4"
		],
		"inline-end": [
			"tw:order-last",
			"tw:shrink-0",
			"tw:[&:not(:empty)]:pr-4"
		],
		"block-start": [
			"tw:order-first",
			"tw:[&:not(:empty)]:w-full",
			"tw:[&:not(:empty)]:px-4",
			"tw:[&:not(:empty)]:py-1.5"
		],
		"block-end": [
			"tw:order-last",
			"tw:[&:not(:empty)]:w-full",
			"tw:[&:not(:empty)]:px-4",
			"tw:[&:not(:empty)]:py-1.5"
		]
	} }
});
const InputGroupAddon = ({ children, align = "inline-start", className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	"data-align": align,
	className: cn(addonVariants({
		align,
		className
	})),
	children
});
//#endregion
//#region src/components/InputGroup/InputGroupButton.tsx
const InputGroupButton = ({ ref, className, tabIndex = -1, ...props }) => /* @__PURE__ */ jsx(Button, {
	...props,
	tabIndex,
	ref,
	variant: "invisible",
	size: "icon-sm",
	className: cn("tw:size-5", "tw:[&_svg]:text-muted-foreground", className)
});
//#endregion
//#region src/components/InputGroup/InputGroupInput.tsx
const InputGroupInput = ({ ref, className, ...inputProps }) => /* @__PURE__ */ jsx("input", {
	...inputProps,
	ref,
	className: cn("tw:flex-1", "tw:min-w-0", "tw:bg-transparent", "tw:outline-hidden", "tw:font-sans", "tw:text-sm", "tw:placeholder:text-neutral-400", "tw:placeholder:font-normal", "tw:disabled:cursor-not-allowed", className)
});
//#endregion
//#region src/components/InputGroup/InputGroupText.tsx
const InputGroupText = ({ className, ...props }) => /* @__PURE__ */ jsx("span", {
	className: cn("tw:flex", "tw:items-center", "tw:gap-2", "tw:text-sm", "tw:text-muted-foreground", "tw:[&_svg]:pointer-events-none", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", className),
	...props
});
//#endregion
//#region src/components/InputGroup/InputGroupTextArea.tsx
const InputGroupTextArea = ({ ref, className, onChange, onValueChange, value, defaultValue, onBlur, onValueBlur, disableResize = false, minRows = 1, maxRows = 4, ...props }) => {
	const handleChange = (event) => {
		onValueChange?.(event.target.value);
		onChange?.(event);
	};
	const handleBlur = (event) => {
		onValueBlur?.(event.target.value);
		onBlur?.(event);
	};
	return /* @__PURE__ */ jsx(TextareaAutosize, {
		...props,
		ref,
		value: value ?? void 0,
		defaultValue: defaultValue ?? void 0,
		onChange: handleChange,
		onBlur: handleBlur,
		minRows,
		maxRows: maxRows === "unlimited" ? void 0 : maxRows,
		className: cn("tw:w-full", "tw:bg-transparent", "tw:outline-hidden", "tw:px-4", "tw:font-sans", "tw:text-sm", "tw:placeholder:text-neutral-400", "tw:placeholder:font-normal", "tw:disabled:cursor-not-allowed", { "tw:resize-none": disableResize }, className)
	});
};
//#endregion
//#region src/components/InputGroup/InputGroup.tsx
const inputGroupVariants = cva([
	"tailwindcss-preflight",
	"tw:group",
	"tw:overflow-hidden",
	"tw:box-border",
	"tw:flex",
	"tw:items-center",
	"tw:min-w-0",
	"tw:rounded",
	"tw:border",
	"tw:border-solid",
	"tw:border-input",
	"tw:text-foreground",
	"tw:transition",
	"tw:has-[:is(input,textarea):focus]:ring-4",
	"tw:has-[:is(input,textarea)[readonly]]:bg-muted",
	"tw:has-[:is(input,textarea)[readonly]]:focus-within:ring-0",
	"tw:has-[:is(input,textarea)[readonly]]:border-input",
	"tw:has-[:is(input,textarea):disabled]:cursor-not-allowed",
	"tw:has-[textarea]:items-start",
	"tw:has-[[data-align=block-start]:not(:empty)]:flex-wrap",
	"tw:has-[[data-align=block-end]:not(:empty)]:flex-wrap",
	"tw:[&>input]:has-[[data-align=block-start]:not(:empty)]:w-auto",
	"tw:[&>input]:has-[[data-align=block-start]:not(:empty)]:flex-1",
	"tw:[&>input]:has-[[data-align=block-end]:not(:empty)]:w-auto",
	"tw:[&>input]:has-[[data-align=block-end]:not(:empty)]:flex-1",
	"tw:[&_input]:px-4",
	"tw:[&>input]:has-[[data-align=inline-start]:not(:empty)]:pl-2",
	"tw:[&>input]:has-[[data-align=inline-end]:not(:empty)]:pr-2"
], {
	defaultVariants: {
		variant: "primary",
		state: "default",
		sizing: "medium",
		focusVariant: "default"
	},
	compoundVariants: [
		{
			variant: "secondary",
			state: "error",
			className: ["tw:bg-error-100", "tw:text-neutral-800"]
		},
		{
			variant: "secondary",
			state: "warning",
			className: ["tw:bg-warning-100", "tw:text-neutral-800"]
		},
		{
			variant: "primary",
			state: "default",
			focusVariant: "off",
			className: ["tw:has-[:is(input,textarea):focus]:border-neutral-200"]
		},
		{
			variant: "secondary",
			state: "default",
			focusVariant: "off",
			className: ["tw:has-[:is(input,textarea):focus]:border-secondary"]
		},
		{
			variant: ["primary", "secondary"],
			state: "error",
			focusVariant: "on",
			className: ["tw:!border-error-500", "tw:!ring-error-200"]
		},
		{
			variant: ["primary", "secondary"],
			state: "warning",
			focusVariant: "on",
			className: ["tw:!border-warning-500", "tw:!ring-warning-200"]
		},
		{
			variant: "argo",
			state: "default",
			className: ["tw:has-[:is(input,textarea):focus]:border-primary", "tw:has-[:is(input,textarea):focus]:ring-ring/50"]
		},
		{
			variant: "argo",
			state: "error",
			className: [
				"tw:border-destructive",
				"tw:has-[:is(input,textarea):focus]:border-destructive",
				"tw:has-[:is(input,textarea):focus]:ring-destructive/20"
			]
		},
		{
			variant: "argo",
			focusVariant: "on",
			className: ["tw:!border-primary", "tw:!ring-ring/50"]
		},
		{
			variant: "argo",
			state: "error",
			focusVariant: "on",
			className: ["tw:!border-destructive", "tw:!ring-destructive/20"]
		},
		{
			variant: "argo",
			state: "default",
			focusVariant: "off",
			className: ["tw:has-[:is(input,textarea):focus]:border-input"]
		}
	],
	variants: {
		focusVariant: {
			default: [],
			on: [
				"tw:!border-ring",
				"tw:!ring-ring/30",
				"tw:!ring-4"
			],
			off: ["tw:has-[:is(input,textarea):focus]:!ring-0"]
		},
		variant: {
			primary: [
				"tw:bg-input-background",
				"tw:text-foreground",
				"tw:has-[:is(input,textarea):disabled]:opacity-40",
				"tw:has-[:is(input,textarea)[aria-invalid=true]]:border-error-500",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:border-error-500",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:ring-error-200"
			],
			secondary: [
				"tw:bg-secondary",
				"tw:border-secondary",
				"tw:has-[:is(input,textarea):disabled]:opacity-40",
				"tw:has-[:is(input,textarea)[aria-invalid=true]]:border-error-500",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:border-error-500",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:ring-error-200"
			],
			argo: [
				"tw:rounded-[var(--radius-lg,0.25rem)]",
				"tw:bg-muted",
				"tw:border-input",
				"tw:text-foreground",
				"tw:has-[:is(input,textarea):disabled]:opacity-50",
				"tw:has-[:is(input,textarea)[readonly]]:bg-muted",
				"tw:has-[:is(input,textarea)[readonly]]:border-input",
				"tw:has-[:is(input,textarea)[readonly]:focus]:border-input",
				"tw:has-[:is(input,textarea)[aria-invalid=true]]:border-destructive",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:border-destructive",
				"tw:has-[:is(input,textarea)[aria-invalid=true]:focus]:ring-destructive/20"
			]
		},
		sizing: {
			default: [
				"tw:[&_:is(input,textarea)]:py-2.5",
				"tw:[&_:is(input,textarea)]:leading-[18px]",
				"tw:[&_textarea]:!min-h-[calc(2.5rem-2px)]"
			],
			small: [
				"tw:[&_:is(input,textarea)]:py-2",
				"tw:[&_:is(input,textarea)]:leading-[16px]",
				"tw:[&_textarea]:!min-h-[calc(2rem-2px)]"
			],
			medium: [
				"tw:[&_:is(input,textarea)]:py-2",
				"tw:[&_:is(input,textarea)]:leading-[18px]",
				"tw:[&_textarea]:!min-h-[calc(2.25rem-2px)]"
			]
		},
		state: {
			default: ["tw:has-[:is(input,textarea):focus]:border-ring", "tw:has-[:is(input,textarea):focus]:ring-ring/30"],
			error: [
				"tw:border-error-500",
				"tw:has-[:is(input,textarea):focus]:border-error-500",
				"tw:has-[:is(input,textarea):focus]:ring-error-200"
			],
			warning: [
				"tw:border-warning-500",
				"tw:has-[:is(input,textarea):focus]:border-warning-500",
				"tw:has-[:is(input,textarea):focus]:ring-warning-200"
			]
		}
	}
});
const InputGroupRoot = ({ ref, children, state, variant, sizing, focusVariant, className, ...props }) => {
	const effectiveVariant = useArgoDesign() ? "argo" : variant;
	const effectiveSizing = effectiveVariant === "argo" ? "medium" : sizing;
	return /* @__PURE__ */ jsx("div", {
		...props,
		ref,
		className: cn(inputGroupVariants({
			state,
			variant: effectiveVariant,
			sizing: effectiveSizing,
			focusVariant,
			className
		})),
		children
	});
};
const InputGroup = InputGroupRoot;
InputGroup.Input = InputGroupInput;
InputGroup.Addon = InputGroupAddon;
InputGroup.Button = InputGroupButton;
InputGroup.TextArea = InputGroupTextArea;
InputGroup.Text = InputGroupText;
//#endregion
export { InputGroup };

//# sourceMappingURL=InputGroup-DzAlYrM8.js.map